import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import { ImportBranchDto, PortOfEntryDto, portOfEntryValidationSchema } from 'dtos'
import * as controllers from 'controllers'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'

interface PortOfEntryMaintenanceAddEditDialogProps {
  initialValues: PortOfEntryDto
  isLoading?: boolean
  open: boolean
  onClose: () => void
  onSave: (values: PortOfEntryDto) => void
}

export default function PortOfEntryMaintenanceAddEditDialog({
  isLoading,
  initialValues,
  open,
  onClose,
  onSave
}: PortOfEntryMaintenanceAddEditDialogProps) {
  const [importBranches, setImportBranches] = useState<ImportBranchDto[]>([])
  const [isGettingImportBranches, setIsGettingImportBranches] = useState(false)

  useEffect(() => {
    if (open && importBranches.length === 0) {
      setIsGettingImportBranches(true)
      controllers
        .getImportBranches()
        .then(response => {
          setImportBranches(response.value)
        })
        .finally(() => {
          setIsGettingImportBranches(false)
        })
    }
  }, [open, importBranches.length])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validateOnChange
        validateOnBlur
        validationSchema={portOfEntryValidationSchema}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Ports of Entry Maintenance</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(touched.cityState && errors.cityState)}
                      helperText={touched.cityState && errors.cityState}
                      label='Port of Entry'
                      name='cityState'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.cityState}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(touched.portCode && errors.portCode)}
                      helperText={touched.portCode && errors.portCode}
                      label='Port Code'
                      name='portCode'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.portCode}
                    />
                  </Grid>

                  {isGettingImportBranches && (
                    <Grid item xs={12} container alignItems='center' justifyContent='center'>
                      <Grid>
                        <CircularProgress />
                      </Grid>
                    </Grid>
                  )}

                  {!isGettingImportBranches &&
                    importBranches.map(importBranch => (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.importBranches?.some(
                                valueImportBranch => valueImportBranch.importBranchGuid === importBranch.importBranchGuid
                              )}
                              disabled={isLoading || isSubmitting}
                              onChange={e => {
                                if (
                                  values.importBranches?.some(
                                    valueImportBranch => valueImportBranch.importBranchGuid === importBranch.importBranchGuid
                                  )
                                ) {
                                  setFieldValue(
                                    'importBranches',
                                    values.importBranches.filter(
                                      valueImportBranch => valueImportBranch.importBranchGuid !== importBranch.importBranchGuid
                                    )
                                  )
                                } else {
                                  setFieldValue('importBranches', [...(values.importBranches ?? []), importBranch])
                                }
                              }}
                            />
                          }
                          label={importBranch.name}
                        />
                      </Grid>
                    ))}
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <ExplanationAccordion variant='outlined'>
                    ||All fields are required. || Port of entry should be formatted as City, State.|| Port code must be a
                    four-digit number. ||
                  </ExplanationAccordion>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={isLoading || isSubmitting} variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
